import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, Download, FileDown, Link2, Check, ChevronsUpDown, X, Wand2, Mountain, Scissors, Pencil, GitCompare } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { listFiles, getFile, profileLines, detectStrings, findAssociatedString, downloadProfilePdf, listSurfaces, listStandards, computeCrossSection, updateAssetField, getFileDiff } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";

function geomOf(a) {
  const f = a.fields || {};
  const g = (f.Geometry || f.GeometryType || "").toString().toLowerCase();
  if (g.includes("poly")) return "Polygon";
  if (g.includes("line") || g.includes("string")) return "Line";
  if (g.includes("point")) return "Point";
  const keys = Object.keys(f).map((k) => k.toLowerCase());
  if (keys.some((k) => k.includes("area"))) return "Polygon";
  if (keys.some((k) => k.includes("length") || k.startsWith("us_"))) return "Line";
  return "Point";
}

const GRAT_CATALOG = {
  id: { label: "Pit / Node", kind: "node", val: (n) => n.label.replace(/ (US|DS)$/, "") },
  surface: { label: "Surface Level", kind: "node", val: (n) => (n.surface_rl != null ? n.surface_rl.toFixed(3) : "") },
  invert: { label: "Invert Level", kind: "node", val: (n) => (n.invert_rl != null ? n.invert_rl.toFixed(3) : "") },
  cover: { label: "Cover (m)", kind: "node", val: (n) => (n.cover_m != null ? n.cover_m.toFixed(2) : (n.surface_rl != null && n.invert_rl != null && n.obvert_rl != null ? (n.surface_rl - n.obvert_rl).toFixed(2) : "")) },
  chainage: { label: "Chainage (m)", kind: "node", val: (n) => n.chainage.toFixed(1) },
  grade: { label: "Grade %", kind: "seg", val: (s) => (s.grade_pct != null ? `${s.grade_pct}` : "") },
  pipe: { label: "Pipe Ø / Mat.", kind: "seg", val: (s) => `${s.diameter_mm || "?"}${s.material ? " " + s.material : ""}` },
  length: { label: "Length (m)", kind: "seg", val: (s) => (s.length_m != null ? `${s.length_m}` : "") },
  existing: { label: "Existing Surface", kind: "node", val: (n) => (n.existing_rl != null ? n.existing_rl.toFixed(3) : "—") },
  design: { label: "Design Surface", kind: "node", val: (n) => (n.design_rl != null ? n.design_rl.toFixed(3) : "—") },
};
const DEFAULT_GRAT = ["id", "surface", "invert", "cover", "pipe", "grade", "chainage"];
const BUILTIN_TEMPLATES = {
  "Standard (default)": DEFAULT_GRAT,
  "Minimal": ["id", "invert", "grade", "chainage"],
  "Full + TIN": ["id", "existing", "design", "surface", "invert", "cover", "pipe", "grade", "length", "chainage"],
};
const TPL_KEY = "adac-graticule-templates";
const loadTemplates = () => {
  try { return { ...BUILTIN_TEMPLATES, ...JSON.parse(localStorage.getItem(TPL_KEY) || "{}") }; }
  catch { return { ...BUILTIN_TEMPLATES }; }
};
const saveTemplate = (name, rows) => {
  const user = JSON.parse(localStorage.getItem(TPL_KEY) || "{}");
  user[name] = rows;
  localStorage.setItem(TPL_KEY, JSON.stringify(user));
};

export default function ProfilePage() {
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState("");
  const [lineAssets, setLineAssets] = useState([]);
  const [chain, setChain] = useState([]); // ordered asset_ids
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exag, setExag] = useState(5);
  const [matchInvert, setMatchInvert] = useState(true);
  const [strings, setStrings] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [gratRows, setGratRows] = useState(DEFAULT_GRAT);
  const [templates, setTemplates] = useState(loadTemplates());
  const [tplName, setTplName] = useState("Standard (default)");
  const [surfaces, setSurfaces] = useState([]);
  const [existingSurf, setExistingSurf] = useState("none");
  const [designSurf, setDesignSurf] = useState("none");
  const [refresh, setRefresh] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const lastIdx = useRef(null);
  const { activeProject } = useProject();

  const [pairOther, setPairOther] = useState("");
  const [corridor, setCorridor] = useState(5);
  const [pairing, setPairing] = useState(false);
  const [paired, setPaired] = useState(null);
  const [pairMode, setPairMode] = useState("pick"); // 'pick' | 'auto'
  const [cmpStrings, setCmpStrings] = useState([]);
  const [cmpDetecting, setCmpDetecting] = useState(false);
  const [standards, setStandards] = useState([]);
  const [stdId, setStdId] = useState("");
  const { user, isPro } = useAuth();
  const lgaLocked = !isPro && !!user?.lga_schema;

  useEffect(() => {
    listStandards().then((s) => {
      setStandards(s);
      const def = (!isPro && user?.lga_schema) ? s.find((x) => x.id === user.lga_schema) : (s.find((x) => x.is_default) || s[0]);
      if (def) setStdId((prev) => prev || def.id);
    }).catch(() => {});
  }, [isPro, user?.lga_schema]);

  useEffect(() => { if (lgaLocked) setStdId(user.lga_schema); }, [lgaLocked, user?.lga_schema]);

  const ilTol = useMemo(() => {
    const st = standards.find((s) => s.id === stdId);
    const r = st?.rules?.find((x) => x.field === "InvertLevel_m");
    return r ? r.value : null;
  }, [standards, stdId]);

  const findPaired = async () => {
    if (!pairOther || chain.length === 0) return;
    setPairing(true);
    try {
      const res = await findAssociatedString(fileId, pairOther, chain, Number(corridor) || 5);
      if (res.error) { toast.error(res.error); setPaired(null); }
      else if (!res.count) { toast.info(`No string found within ${corridor} m corridor`); setPaired(null); }
      else { setPaired({ ...res, name: files.find((f) => f.id === pairOther)?.name }); toast.success(`Paired ${res.count} segment(s) · avg offset ${res.avg_offset_m} m`); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Corridor search failed");
    } finally {
      setPairing(false);
    }
  };

  const detectCmpStrings = async () => {
    if (!pairOther) return;
    setCmpDetecting(true);
    try {
      const res = await detectStrings(pairOther);
      setCmpStrings(res.strings || []);
      if (!res.count) toast.info("No line strings detected in that file");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not detect strings");
    } finally {
      setCmpDetecting(false);
    }
  };

  const pickCmpString = async (ids) => {
    setPairing(true);
    try {
      const prof = await profileLines(pairOther, ids, {});
      setPaired({ asset_ids: ids, count: ids.length, profile: prof, file_name: files.find((f) => f.id === pairOther)?.name, manual: true });
      toast.success(`Overlaid ${ids.length}-segment comparison string`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not profile comparison string");
    } finally {
      setPairing(false);
    }
  };

  // Comparison invert interpolation + design-vs-as-constructed Δ stats
  const compInvertAt = useMemo(() => {
    if (!paired?.profile) return null;
    const pts = buildSeries(paired.profile, matchInvert).invert.slice().sort((a, b) => a.ch - b.ch);
    if (pts.length === 0) return null;
    return (ch) => {
      if (ch <= pts[0].ch) return pts[0].rl;
      if (ch >= pts[pts.length - 1].ch) return pts[pts.length - 1].rl;
      for (let i = 0; i < pts.length - 1; i++) {
        if (ch >= pts[i].ch && ch <= pts[i + 1].ch) {
          const span = pts[i + 1].ch - pts[i].ch || 1;
          return pts[i].rl + ((ch - pts[i].ch) / span) * (pts[i + 1].rl - pts[i].rl);
        }
      }
      return null;
    };
  }, [paired, matchInvert]);

  const deltaStats = useMemo(() => {
    if (!compInvertAt || !profile) return null;
    const ds = profile.nodes
      .filter((n) => n.invert_rl != null)
      .map((n) => n.invert_rl - compInvertAt(n.chainage))
      .filter((v) => v != null && !Number.isNaN(v));
    if (!ds.length) return null;
    const abs = ds.map(Math.abs);
    const within = ilTol != null ? abs.filter((d) => d <= ilTol).length : null;
    return { max: Math.max(...abs).toFixed(3), avg: (abs.reduce((a, b) => a + b, 0) / abs.length).toFixed(3), n: ds.length, within };
  }, [compInvertAt, profile, ilTol]);

  useEffect(() => {
    if (!activeProject) return;
    const fid = activeProject.survey_file_id || activeProject.design_file_id;
    if (fid) setFileId(fid);
    if (activeProject.existing_surface_id) setExistingSurf(activeProject.existing_surface_id);
    if (activeProject.design_surface_id) setDesignSurf(activeProject.design_surface_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  useEffect(() => { listFiles().then(setFiles); listSurfaces().then(setSurfaces); }, []);

  useEffect(() => {
    if (!fileId) return;
    setChain([]); setProfile(null); lastIdx.current = null; setStrings([]); setPaired(null);
    getFile(fileId).then((doc) => {
      setLineAssets(doc.assets.filter((a) => geomOf(a) === "Line"));
    });
  }, [fileId]);

  useEffect(() => {
    if (chain.length === 0) { setProfile(null); return; }
    setPaired(null);
    setLoading(true);
    profileLines(fileId, chain, {
      existing_surface_id: existingSurf !== "none" ? existingSurf : null,
      design_surface_id: designSurf !== "none" ? designSurf : null,
    })
      .then(setProfile)
      .catch((e) => toast.error(e?.response?.data?.detail || "Profile failed"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, existingSurf, designSurf, refresh]);

  const saveLevel = async (assetId, field, value) => {
    try {
      await updateAssetField(fileId, assetId, field, value);
      toast.success(`${assetId} · ${field} → ${value}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not write level to XML");
    }
  };

  const openDiff = async () => {
    try {
      const d = await getFileDiff(fileId);
      setDiff(d);
      setDiffOpen(true);
    } catch {
      toast.error("Could not load XML diff");
    }
  };

  const toggleAsset = (aid, idx, shift) => {
    if (shift && lastIdx.current != null) {
      const [a, b] = [lastIdx.current, idx].sort((x, y) => x - y);
      const range = lineAssets.slice(a, b + 1).map((x) => x.asset_id);
      setChain((c) => [...c, ...range.filter((r) => !c.includes(r))]);
    } else {
      setChain((c) => (c.includes(aid) ? c.filter((x) => x !== aid) : [...c, aid]));
    }
    lastIdx.current = idx;
  };

  const applyTemplate = (name) => { setTplName(name); if (templates[name]) setGratRows(templates[name]); };
  const toggleRow = (key) => { setTplName("Custom"); setGratRows((r) => (r.includes(key) ? r.filter((x) => x !== key) : [...r, key])); };
  const saveAs = () => {
    const name = window.prompt("Save graticule template as (LGA name):");
    if (!name) return;
    saveTemplate(name, gratRows);
    setTemplates(loadTemplates());
    setTplName(name);
    toast.success(`Template "${name}" saved`);
  };

  const removeFromChain = (aid) => setChain((c) => c.filter((x) => x !== aid));

  const detect = async () => {
    if (!fileId) return;
    setDetecting(true);
    try {
      const r = await detectStrings(fileId);
      setStrings(r.strings);
      if (r.strings.length === 0) toast.info("No connectable pipe strings found");
      else toast.success(`${r.strings.length} pipe string(s) detected`);
    } catch {
      toast.error("Detect failed");
    } finally {
      setDetecting(false);
    }
  };

  const exportPDF = async () => {
    if (!profile) return;
    setPdfBusy(true);
    try {
      await downloadProfilePdf({
        file_id: fileId, asset_ids: chain, exaggeration: exag, graticule_rows: gratRows,
        existing_surface_id: existingSurf !== "none" ? existingSurf : null,
        design_surface_id: designSurf !== "none" ? designSurf : null,
        compare_file_id: paired ? pairOther : null,
        compare_asset_ids: paired ? paired.asset_ids : null,
        compare_name: paired ? paired.file_name : null,
        standard_id: paired ? stdId : null,
      }, `LongSection_${profile.file_name}.pdf`);
      toast.success("Long-section PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const exportCSV = () => {
    if (!profile) return;
    const rows = [["segment", "type", "start_ch_m", "end_ch_m", "length_m", "us_invert_m", "ds_invert_m", "us_surface_m", "ds_surface_m", "grade_pct", "diameter_mm", "material", "cover_us_m", "cover_ds_m"]];
    profile.segments.forEach((s) => rows.push([s.asset_id, s.type, s.start_chainage, s.end_chainage, s.length_m, s.us_invert_m, s.ds_invert_m, s.us_surface_m, s.ds_surface_m, s.grade_pct, s.diameter_mm, s.material, s.cover_us_m, s.cover_ds_m]));
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `LongSection_${profile.file_name}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Long-Section Profile</h1>
          <p className="font-mono text-[11px] text-zinc-500">Chain line assets (pipes/drains) into a long-section — invert, surface &amp; pit envelopes</p>
        </div>
        <Select value={fileId} onValueChange={setFileId}>
          <SelectTrigger data-testid="profile-file-select" className="w-64 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
            <SelectValue placeholder="Select a document…" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
            {files.map((f) => (<SelectItem key={f.id} value={f.id} className="font-mono text-xs">{f.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </header>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Asset chain picker */}
        <div className="border border-zinc-800 rounded-sm bg-[#101010] overflow-hidden flex flex-col" style={{ maxHeight: "78vh" }} data-testid="profile-line-list">
          <div className="px-3 py-2 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            Line assets ({lineAssets.length})
          </div>
          <div className="px-3 py-2 font-mono text-[10px] text-zinc-600 border-b border-zinc-900">
            Click to add to the chain in order. <span className="text-zinc-400">Shift-click</span> to add a contiguous run.
          </div>
          <div className="p-2 border-b border-zinc-900 space-y-2">
            <Button size="sm" data-testid="detect-strings-btn" onClick={detect} disabled={!fileId || detecting} className="w-full h-8 rounded-sm bg-blue-600 hover:bg-blue-500 text-white font-mono text-[11px]">
              {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />} Auto-detect pipe strings
            </Button>
            {strings.length > 0 && (
              <div className="space-y-1" data-testid="detected-strings">
                {strings.map((s, i) => (
                  <button key={i} data-testid={`string-${i}`} onClick={() => { setChain(s.asset_ids); lastIdx.current = null; }}
                    className="w-full text-left px-2 py-1.5 rounded-sm border border-zinc-800 bg-zinc-900/40 hover:border-blue-500 font-mono text-[10px] text-zinc-300">
                    <span className="text-blue-400">String {i + 1}</span> · {s.count} seg · {s.type}
                    <div className="text-zinc-600 truncate">{s.asset_ids.join(" → ")}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="overflow-auto flex-1 p-2 space-y-1">
            {lineAssets.length === 0 ? (
              <p className="font-mono text-[11px] text-zinc-600 p-2">{fileId ? "No line assets in this file." : "Select a document."}</p>
            ) : lineAssets.map((a, idx) => {
              const order = chain.indexOf(a.asset_id);
              const sel = order >= 0;
              return (
                <button
                  key={a.asset_id}
                  data-testid={`profile-line-${a.asset_id}`}
                  onClick={(e) => toggleAsset(a.asset_id, idx, e.shiftKey)}
                  className={`w-full text-left px-3 py-2 rounded-sm border transition-colors duration-75 ${sel ? "border-blue-500 bg-blue-600/15" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-zinc-200">{a.asset_id}</span>
                    {sel && <span className="font-mono text-[10px] w-5 h-5 rounded-sm bg-blue-600 text-white flex items-center justify-center">{order + 1}</span>}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{a.type}</div>
                </button>
              );
            })}
          </div>
          {chain.length > 0 && (
            <div className="border-t border-zinc-800 p-2">
              <Button size="sm" variant="ghost" onClick={() => setChain([])} className="w-full h-7 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-800 font-mono text-[11px]">Clear chain</Button>
            </div>
          )}
        </div>

        {/* Profile viewer */}
        <div className="space-y-3">
          {/* controls */}
          <div className="flex flex-wrap items-center gap-4 p-3 rounded-sm border border-zinc-800 bg-[#101010]">
            <div className="flex items-center gap-2">
              <ChevronsUpDown className="w-4 h-4 text-zinc-500" />
              <label className="font-mono text-[11px] text-zinc-500">Vertical exaggeration</label>
              <input type="range" min="1" max="25" step="1" value={exag} onChange={(e) => setExag(parseInt(e.target.value))} data-testid="exag-slider" className="w-44 accent-blue-600" />
              <span className="font-mono text-[11px] text-zinc-300 w-12" data-testid="exag-value">{exag}× V:H</span>
            </div>
            <button
              data-testid="match-invert-toggle"
              onClick={() => setMatchInvert((m) => !m)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] border transition-colors duration-75 ${matchInvert ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"}`}
            >
              <Link2 className="w-3.5 h-3.5" /> Match invert at junctions {matchInvert ? "ON" : "OFF"}
            </button>
            <Button size="sm" onClick={exportPDF} disabled={!profile || pdfBusy} className="ml-auto h-9 rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {pdfBusy ? "PDF…" : "PDF"}
            </Button>
            <Button size="sm" onClick={exportCSV} disabled={!profile} variant="outline" className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300">
              <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
            </Button>
            <button
              data-testid="edit-levels-toggle"
              onClick={() => setEditMode((m) => !m)}
              disabled={!profile}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] border transition-colors duration-75 disabled:opacity-40 ${editMode ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"}`}
            >
              <Pencil className="w-3.5 h-3.5" /> Edit inverts {editMode ? "ON" : "OFF"}
            </button>
            <Button size="sm" onClick={openDiff} disabled={!fileId} variant="outline" data-testid="xml-diff-btn" className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300">
              <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Raw-XML diff
            </Button>
          </div>

          {/* selected chain chips */}
          {chain.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="profile-chain">
              {chain.map((aid, i) => (
                <span key={aid} className="inline-flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-700 text-zinc-300">
                  <span className="text-blue-400">{i + 1}</span>{aid}
                  <button onClick={() => removeFromChain(aid)} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 font-mono text-sm p-8"><Loader2 className="w-4 h-4 animate-spin" /> Computing long-section…</div>
          ) : profile && profile.segments.length > 0 ? (
            <>
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-sm border border-zinc-800 bg-[#101010]" data-testid="surface-selectors">
                <span className="font-mono text-[11px] text-zinc-500 flex items-center gap-1.5"><Mountain className="w-3.5 h-3.5" /> Surfaces</span>
                <Select value={existingSurf} onValueChange={setExistingSurf}>
                  <SelectTrigger data-testid="existing-surface" className="w-48 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Existing ground…" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                    <SelectItem value="none" className="font-mono text-[11px]">No existing surface</SelectItem>
                    {surfaces.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name} ({s.role})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={designSurf} onValueChange={setDesignSurf}>
                  <SelectTrigger data-testid="design-surface" className="w-48 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Design surface…" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                    <SelectItem value="none" className="font-mono text-[11px]">No design surface</SelectItem>
                    {surfaces.map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name} ({s.role})</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="font-mono text-[10px] text-zinc-600">existing = green · design = purple. Sampled at each pit (needs pit coords / US_Pit links).</span>
              </div>
              <div className="p-3 rounded-sm border border-zinc-800 bg-[#101010] space-y-2.5" data-testid="corridor-panel">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-[11px] text-zinc-500 flex items-center gap-1.5"><GitCompare className="w-3.5 h-3.5" /> Compare against (design ↔ as-constructed)</span>
                  <div className="flex rounded-sm border border-zinc-700 overflow-hidden">
                    <button data-testid="pairmode-pick" onClick={() => setPairMode("pick")} className={`px-2.5 py-1 font-mono text-[10px] ${pairMode === "pick" ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-900"}`}>Pick string</button>
                    <button data-testid="pairmode-auto" onClick={() => setPairMode("auto")} className={`px-2.5 py-1 font-mono text-[10px] border-l border-zinc-700 ${pairMode === "auto" ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-900"}`}>Corridor auto</button>
                  </div>
                  <Select value={pairOther} onValueChange={(v) => { setPairOther(v); setCmpStrings([]); }}>
                    <SelectTrigger data-testid="pair-file" className="w-56 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Design / as-constructed file…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                      {files.filter((f) => f.id !== fileId).map((f) => <SelectItem key={f.id} value={f.id} className="font-mono text-[11px]">{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {pairMode === "auto" ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <label className="font-mono text-[10px] text-zinc-500">Corridor (m)</label>
                        <Input data-testid="corridor-width" type="number" min="0" step="0.5" value={corridor} onChange={(e) => setCorridor(e.target.value)} className="w-20 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]" />
                      </div>
                      <Button size="sm" onClick={findPaired} disabled={pairing || !pairOther} data-testid="find-paired-btn" className="h-8 rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
                        {pairing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <GitCompare className="w-3.5 h-3.5 mr-1.5" />} Find within corridor
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={detectCmpStrings} disabled={cmpDetecting || !pairOther} data-testid="detect-cmp-btn" className="h-8 rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
                      {cmpDetecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null} Detect strings
                    </Button>
                  )}
                  {paired && (
                    <span className="font-mono text-[10px] text-amber-400">
                      {paired.manual ? `${paired.count} seg overlaid` : `${paired.count} seg · avg ${paired.avg_offset_m} m · max ${paired.max_offset_m} m`}
                      {deltaStats && <> · ΔIL avg {deltaStats.avg} m · max {deltaStats.max} m</>}
                      {deltaStats?.within != null && <span className={deltaStats.within === deltaStats.n ? "text-emerald-400" : "text-red-400"}> · {deltaStats.within}/{deltaStats.n} within ±{ilTol} m</span>}
                      <button onClick={() => setPaired(null)} className="ml-1.5 text-zinc-500 hover:text-red-400">clear</button>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-zinc-500">Conformance standard (vertical tol.)</span>
                  <Select value={stdId} onValueChange={setStdId} disabled={lgaLocked}>
                    <SelectTrigger data-testid="profile-standard-select" className="w-56 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Standard…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">
                      {(lgaLocked ? standards.filter((s) => s.id === user.lga_schema) : standards).map((s) => <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {ilTol != null && <span className="font-mono text-[10px] text-zinc-500">±{ilTol} m IL</span>}
                </div>
                {pairMode === "pick" && cmpStrings.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="cmp-string-list">
                    {cmpStrings.map((s, i) => (
                      <button key={i} data-testid={`cmp-string-${i}`} onClick={() => pickCmpString(s.asset_ids)} disabled={pairing}
                        className="px-2 py-1 rounded-sm font-mono text-[10px] border border-zinc-700 text-zinc-300 hover:bg-blue-600/15 hover:border-blue-500/50 transition-colors duration-75">
                        {s.label || `String ${i + 1}`} · {s.count} seg
                      </button>
                    ))}
                  </div>
                )}
                {!paired && <span className="font-mono text-[10px] text-zinc-600">Overlay the matching string from the other file to compare design vs as-constructed inverts.</span>}
              </div>
              <LongSection profile={profile} exag={exag} matchInvert={matchInvert} paired={paired?.profile} pairedName={paired?.file_name} />
              <CrossSectionPanel fileId={fileId} chain={chain} nodes={profile.nodes} existingSurf={existingSurf} designSurf={designSurf} />
              <GraticuleControls templates={templates} tplName={tplName} gratRows={gratRows}
                onApply={applyTemplate} onToggle={toggleRow} onSave={saveAs} />
              <Graticule profile={profile} rows={gratRows} compInvertAt={compInvertAt} compName={paired?.file_name} ilTol={paired ? ilTol : null} />
              <SegmentTable profile={profile} editMode={editMode} onSave={saveLevel} />
            </>
          ) : (
            <div className="grid-texture border border-zinc-800 rounded-sm py-24 flex flex-col items-center text-center">
              <Activity className="w-10 h-10 text-zinc-700 mb-4" />
              <p className="text-sm text-zinc-500 max-w-md">Select a document, then click pipe/drain (line) assets on the left to chain them into a long-section. Invert and surface levels with pit envelopes are drawn to scale.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-sm max-w-3xl" data-testid="xml-diff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Raw-XML diff</DialogTitle>
            <DialogDescription className="font-mono text-xs text-zinc-500">
              {diff?.edited ? `${diff.added} added · ${diff.removed} removed line(s) since first edit` : "No edits yet — original equals current"}
            </DialogDescription>
          </DialogHeader>
          <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] overflow-auto max-h-[60vh]" data-testid="xml-diff-body">
            {diff && diff.diff.length > 0 ? (
              <pre className="font-code text-[11px] leading-relaxed p-3 m-0">
                {diff.diff.map((ln, i) => {
                  const isAdd = ln.startsWith("+") && !ln.startsWith("+++");
                  const isDel = ln.startsWith("-") && !ln.startsWith("---");
                  const isHunk = ln.startsWith("@@");
                  return (
                    <div key={i} className={isAdd ? "text-emerald-400 bg-emerald-500/5" : isDel ? "text-red-400 bg-red-500/5" : isHunk ? "text-blue-400" : "text-zinc-500"}>{ln || " "}</div>
                  );
                })}
              </pre>
            ) : (
              <p className="font-mono text-[11px] text-zinc-500 p-4">No differences — the stored XML has not been edited from its original.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function buildSeries(profile, matchInvert) {
  // Two points per segment captures invert "steps" at junctions
  const invert = [];
  const surface = [];
  let prevDs = null;
  profile.segments.forEach((s) => {
    const us = matchInvert && prevDs != null && s.us_invert_m != null ? prevDs : s.us_invert_m;
    invert.push({ ch: s.start_chainage, rl: us });
    invert.push({ ch: s.end_chainage, rl: s.ds_invert_m });
    if (s.us_surface_m != null) surface.push({ ch: s.start_chainage, rl: s.us_surface_m });
    if (s.ds_surface_m != null) surface.push({ ch: s.end_chainage, rl: s.ds_surface_m });
    prevDs = s.ds_invert_m;
  });
  // junction steps (data quality flag)
  const steps = [];
  for (let i = 1; i < profile.segments.length; i++) {
    const a = profile.segments[i - 1].ds_invert_m, b = profile.segments[i].us_invert_m;
    if (a != null && b != null && Math.abs(a - b) > 0.001) {
      steps.push({ ch: profile.segments[i].start_chainage, step: +(b - a).toFixed(3) });
    }
  }
  return { invert: invert.filter((p) => p.rl != null), surface, steps };
}

function LongSection({ profile, exag, matchInvert, paired, pairedName }) {
  const { invert, surface, steps } = useMemo(() => buildSeries(profile, matchInvert), [profile, matchInvert]);
  const pairedSeries = useMemo(() => (paired ? buildSeries(paired, matchInvert) : null), [paired, matchInvert]);
  const length = profile.summary.total_length_m || 1;

  const rls = [...invert.map((p) => p.rl), ...surface.map((p) => p.rl),
    ...(pairedSeries ? pairedSeries.invert.map((p) => p.rl) : [])].filter((v) => v != null);
  const minRL = Math.floor((Math.min(...rls) - 0.5) * 2) / 2;
  const maxRL = Math.ceil((Math.max(...rls) + 0.5) * 2) / 2;

  const mL = 84, mR = 24, mT = 28, mB = 52;
  const innerW = 860;
  const pxPerM_h = innerW / length;
  const pxPerM_v = pxPerM_h * exag;
  const innerH = Math.max(120, (maxRL - minRL) * pxPerM_v);
  const W = mL + innerW + mR;
  const H = mT + innerH + mB;

  const xOf = (ch) => mL + ch * pxPerM_h;
  const yOf = (rl) => mT + (maxRL - rl) * pxPerM_v;

  const invertPath = invert.map((p, i) => `${i ? "L" : "M"}${xOf(p.ch)},${yOf(p.rl)}`).join(" ");
  const surfacePath = surface.map((p, i) => `${i ? "L" : "M"}${xOf(p.ch)},${yOf(p.rl)}`).join(" ");
  const pairedPath = pairedSeries ? pairedSeries.invert.map((p, i) => `${i ? "L" : "M"}${xOf(p.ch)},${yOf(p.rl)}`).join(" ") : null;

  // grid RL ticks
  const ticks = [];
  for (let rl = minRL; rl <= maxRL + 1e-6; rl += 0.5) ticks.push(+rl.toFixed(2));
  // chainage ticks every ~ sensible interval
  const chStep = length > 120 ? 25 : length > 50 ? 10 : 5;
  const chTicks = [];
  for (let c = 0; c <= length + 1e-6; c += chStep) chTicks.push(+c.toFixed(2));
  if (chTicks[chTicks.length - 1] < length - 0.01) chTicks.push(+length.toFixed(2));

  return (
    <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] overflow-auto" data-testid="long-section-svg" style={{ maxHeight: "62vh" }}>
      <svg width={W} height={H} style={{ display: "block", minWidth: "100%" }}>
        {/* grid */}
        {ticks.map((rl) => (
          <g key={rl}>
            <line x1={mL} y1={yOf(rl)} x2={mL + innerW} y2={yOf(rl)} stroke="#1f2937" strokeWidth="1" />
            <text x={mL - 8} y={yOf(rl) + 3} fontSize="9" fontFamily="monospace" fill="#71717a" textAnchor="end">{rl.toFixed(2)}</text>
          </g>
        ))}
        {chTicks.map((c) => (
          <g key={c}>
            <line x1={xOf(c)} y1={mT} x2={xOf(c)} y2={mT + innerH} stroke="#161b22" strokeWidth="1" />
            <text x={xOf(c)} y={mT + innerH + 16} fontSize="9" fontFamily="monospace" fill="#71717a" textAnchor="middle">{c.toFixed(0)}</text>
          </g>
        ))}
        <text x={mL + innerW / 2} y={H - 8} fontSize="10" fontFamily="monospace" fill="#a1a1aa" textAnchor="middle">Chainage (m)</text>
        <text x={14} y={mT + innerH / 2} fontSize="10" fontFamily="monospace" fill="#a1a1aa" textAnchor="middle" transform={`rotate(-90 14 ${mT + innerH / 2})`}>Reduced Level (m AHD)</text>

        {/* pipe bands (obvert = invert + diameter) */}
        {profile.segments.map((s, i) => {
          if (s.us_invert_m == null || s.ds_invert_m == null) return null;
          const dia = (s.diameter_mm || 0) / 1000;
          const x1 = xOf(s.start_chainage), x2 = xOf(s.end_chainage);
          const yUsInv = yOf(s.us_invert_m), yDsInv = yOf(s.ds_invert_m);
          const yUsTop = yOf(s.us_invert_m + dia), yDsTop = yOf(s.ds_invert_m + dia);
          return (
            <polygon key={i} points={`${x1},${yUsInv} ${x2},${yDsInv} ${x2},${yDsTop} ${x1},${yUsTop}`}
              fill="rgba(37,99,235,0.18)" stroke="#3b82f6" strokeWidth="1" />
          );
        })}

        {/* surface line */}
        {surface.length > 1 && <path d={surfacePath} fill="none" stroke="#b45309" strokeWidth="1.8" strokeDasharray="6 4" />}
        {/* paired (corridor-matched) invert line */}
        {pairedPath && <path d={pairedPath} fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeDasharray="5 3" data-testid="paired-invert-line" />}
        {/* invert line */}
        <path d={invertPath} fill="none" stroke="#22d3ee" strokeWidth="2" />

        {/* legend */}
        {pairedPath && (
          <g>
            <line x1={mL + innerW - 220} y1={mT + 8} x2={mL + innerW - 200} y2={mT + 8} stroke="#22d3ee" strokeWidth="2" />
            <text x={mL + innerW - 196} y={mT + 11} fontSize="8.5" fontFamily="monospace" fill="#22d3ee">this string</text>
            <line x1={mL + innerW - 120} y1={mT + 8} x2={mL + innerW - 100} y2={mT + 8} stroke="#f59e0b" strokeWidth="1.8" strokeDasharray="5 3" />
            <text x={mL + innerW - 96} y={mT + 11} fontSize="8.5" fontFamily="monospace" fill="#f59e0b">{(pairedName || "paired").slice(0, 18)}</text>
          </g>
        )}

        {/* pit envelopes at nodes */}
        {profile.nodes.map((n, i) => {
          if (n.invert_rl == null || n.surface_rl == null) return null;
          const x = xOf(n.chainage);
          const yTop = yOf(n.surface_rl), yBot = yOf(n.invert_rl);
          const w = 9;
          return (
            <g key={i}>
              <rect x={x - w / 2} y={yTop} width={w} height={Math.max(2, yBot - yTop)} fill="rgba(113,113,122,0.25)" stroke="#a1a1aa" strokeWidth="1.2" />
              <line x1={x} y1={yTop} x2={x} y2={yBot} stroke="#e4e4e7" strokeWidth="0.8" strokeDasharray="2 2" />
              <circle cx={x} cy={yTop} r="2.5" fill="#b45309" />
              <circle cx={x} cy={yBot} r="2.5" fill="#22d3ee" />
              <text x={x} y={yTop - 6} fontSize="8.5" fontFamily="monospace" fill="#d4d4d8" textAnchor="middle">{n.label.replace(/ (US|DS)$/, "")}</text>
              <text x={x + w / 2 + 3} y={yTop + 9} fontSize="7.5" fontFamily="monospace" fill="#b45309">SL {n.surface_rl.toFixed(2)}</text>
              <text x={x + w / 2 + 3} y={yBot - 2} fontSize="7.5" fontFamily="monospace" fill="#22d3ee">IL {n.invert_rl.toFixed(2)}</text>
            </g>
          );
        })}

        {/* grade labels */}
        {profile.segments.map((s, i) => {
          if (s.grade_pct == null || s.us_invert_m == null || s.ds_invert_m == null) return null;
          const mx = xOf((s.start_chainage + s.end_chainage) / 2);
          const my = yOf((s.us_invert_m + s.ds_invert_m) / 2) + 14;
          return <text key={i} x={mx} y={my} fontSize="8" fontFamily="monospace" fill="#60a5fa" textAnchor="middle">{s.grade_pct}% · Ø{s.diameter_mm || "?"}</text>;
        })}

        {/* junction invert steps */}
        {steps.map((st, i) => (
          <g key={i}>
            <text x={xOf(st.ch)} y={mT + 12} fontSize="8.5" fontFamily="monospace" fill="#f87171" textAnchor="middle">step {st.step > 0 ? "+" : ""}{st.step}m</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function SegmentTable({ profile, editMode, onSave }) {
  const s = profile.summary;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="profile-summary">
        <Stat label="Segments" value={s.segment_count} />
        <Stat label="Total length" value={`${s.total_length_m} m`} />
        <Stat label="Total fall" value={s.total_fall_m != null ? `${s.total_fall_m} m` : "—"} />
        <Stat label="Avg grade" value={s.avg_grade_pct != null ? `${s.avg_grade_pct}%` : "—"} />
        <Stat label="Invert range" value={s.min_invert_m != null ? `${s.min_invert_m}–${s.max_invert_m}` : "—"} />
      </div>
      {editMode && (
        <p className="font-mono text-[11px] text-amber-400/90" data-testid="edit-hint">
          Edit mode — change a US/DS invert and press Enter to write it back to the as-built XML (US_InvertLevel_m / DS_InvertLevel_m).
        </p>
      )}
      <div className="border border-zinc-800 rounded-sm overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="bg-[#101010] text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
              <th className="text-left font-medium px-3 py-2">Segment</th>
              <th className="text-right font-medium px-3 py-2">Length</th>
              <th className="text-right font-medium px-3 py-2">US IL</th>
              <th className="text-right font-medium px-3 py-2">DS IL</th>
              <th className="text-right font-medium px-3 py-2">Grade</th>
              <th className="text-right font-medium px-3 py-2">Ø</th>
              <th className="text-left font-medium px-3 py-2">Material</th>
              <th className="text-right font-medium px-3 py-2">Cover US/DS</th>
            </tr>
          </thead>
          <tbody>
            {profile.segments.map((seg) => (
              <tr key={seg.asset_id} className="border-b border-zinc-900 last:border-0" data-testid={`profile-seg-${seg.asset_id}`}>
                <td className="px-3 py-2 text-zinc-200">{seg.asset_id}</td>
                <td className="px-3 py-2 text-right text-zinc-300">{seg.length_m ?? "—"}</td>
                <td className="px-3 py-2 text-right text-cyan-400">
                  {editMode ? <LevelInput value={seg.us_invert_m} testid={`edit-us-${seg.asset_id}`} onSave={(v) => onSave(seg.asset_id, "US_InvertLevel_m", v)} /> : (seg.us_invert_m ?? "—")}
                </td>
                <td className="px-3 py-2 text-right text-cyan-400">
                  {editMode ? <LevelInput value={seg.ds_invert_m} testid={`edit-ds-${seg.asset_id}`} onSave={(v) => onSave(seg.asset_id, "DS_InvertLevel_m", v)} /> : (seg.ds_invert_m ?? "—")}
                </td>
                <td className="px-3 py-2 text-right text-blue-400">{seg.grade_pct != null ? `${seg.grade_pct}%` : "—"}</td>
                <td className="px-3 py-2 text-right text-zinc-300">{seg.diameter_mm ?? "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{seg.material ?? "—"}</td>
                <td className="px-3 py-2 text-right text-zinc-400">{seg.cover_us_m ?? "—"} / {seg.cover_ds_m ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LevelInput({ value, onSave, testid }) {
  const [v, setV] = useState(value != null ? String(value) : "");
  useEffect(() => { setV(value != null ? String(value) : ""); }, [value]);
  const commit = () => {
    const t = v.trim();
    if (t === "" || t === (value != null ? String(value) : "")) return;
    if (isNaN(parseFloat(t))) { toast.error("Enter a numeric level"); return; }
    onSave(t);
  };
  return (
    <input
      data-testid={testid}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); e.target.blur(); } }}
      onBlur={commit}
      className="w-20 h-7 text-right rounded-sm bg-[#0A0A0A] border border-amber-500/30 focus:border-amber-500 px-2 font-mono text-xs text-amber-300 outline-none"
    />
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="font-display text-lg font-bold text-zinc-100">{value}</div>
    </div>
  );
}

function GraticuleControls({ templates, tplName, gratRows, onApply, onToggle, onSave }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-sm border border-zinc-800 bg-[#101010]" data-testid="graticule-controls">
      <span className="font-mono text-[11px] text-zinc-500">Graticule template</span>
      <Select value={tplName} onValueChange={onApply}>
        <SelectTrigger data-testid="graticule-template" className="w-48 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
          {Object.keys(templates).map((n) => <SelectItem key={n} value={n} className="font-mono text-[11px]">{n}</SelectItem>)}
          {tplName === "Custom" && <SelectItem value="Custom" className="font-mono text-[11px]">Custom</SelectItem>}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-1">
        {Object.entries(GRAT_CATALOG).map(([key, def]) => {
          const on = gratRows.includes(key);
          return (
            <button key={key} data-testid={`grat-row-${key}`} onClick={() => onToggle(key)}
              className={`px-2 py-1 rounded-sm font-mono text-[10px] border transition-colors duration-75 ${on ? "border-blue-500/50 bg-blue-600/15 text-blue-300" : "border-zinc-700 text-zinc-500 hover:bg-zinc-900"}`}>
              {def.label}
            </button>
          );
        })}
      </div>
      <Button size="sm" data-testid="save-template-btn" onClick={onSave} variant="outline" className="ml-auto h-8 rounded-sm border-zinc-700 bg-transparent text-zinc-300 font-mono text-[11px]">Save as LGA template…</Button>
    </div>
  );
}

function Graticule({ profile, rows, compInvertAt, compName, ilTol }) {
  const nodes = profile.nodes;
  const segs = profile.segments;
  const length = profile.summary.total_length_m || 1;
  const mL = 84, mR = 24, innerW = 860, rowH = 22, headH = 22;
  const pxPerM = innerW / length;
  const xOf = (ch) => mL + ch * pxPerM;
  const baseRows = rows.filter((r) => r !== "id");
  const showConf = compInvertAt && ilTol != null;
  const shown = compInvertAt ? [...baseRows, "__delta", ...(showConf ? ["__conf"] : [])] : baseRows;
  const labelOf = (key) => (key === "__delta" ? "Δ IL (D−AC)" : key === "__conf" ? `Conform ±${ilTol}m` : GRAT_CATALOG[key].label);
  const W = mL + innerW + mR;
  const H = headH + shown.length * rowH + 2;

  return (
    <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] overflow-auto" data-testid="graticule-svg">
      <svg width={W} height={H} style={{ display: "block", minWidth: "100%" }}>
        {/* header row: pit/node ids */}
        <rect x={0} y={0} width={W} height={headH} fill="#0b1f3a" />
        {nodes.map((n, i) => (
          <text key={i} x={i === 0 ? xOf(n.chainage) + 5 : xOf(n.chainage)} y={headH - 7} fontSize="8.5" fontFamily="monospace" fill="#fff" textAnchor={i === 0 ? "start" : "middle"}>{n.label.replace(/ (US|DS)$/, "")}</text>
        ))}
        {/* vertical station guides */}
        {nodes.map((n, i) => (
          <line key={i} x1={xOf(n.chainage)} y1={0} x2={xOf(n.chainage)} y2={H} stroke="#1f2937" strokeWidth="0.7" />
        ))}
        {/* rows (values first) */}
        {shown.map((key, r) => {
          const def = key === "__delta" || key === "__conf" ? { kind: "node" } : GRAT_CATALOG[key];
          const y = headH + r * rowH;
          return (
            <g key={key}>
              <rect x={0} y={y} width={W} height={rowH} fill={key === "__delta" ? "#1a1206" : key === "__conf" ? "#0a1410" : (r % 2 ? "#0e0e0e" : "#121212")} />
              <line x1={0} y1={y} x2={W} y2={y} stroke="#1f2937" strokeWidth="0.5" />
              {key === "__delta"
                ? nodes.map((n, i) => {
                    const d = n.invert_rl != null ? n.invert_rl - compInvertAt(n.chainage) : null;
                    return (
                      <text key={i} x={i === 0 ? xOf(n.chainage) + 5 : xOf(n.chainage)} y={y + rowH - 7} fontSize="8" fontFamily="monospace"
                        fill={d == null ? "#52525b" : (ilTol != null ? (Math.abs(d) > ilTol ? "#f87171" : "#34d399") : "#fbbf24")} textAnchor={i === 0 ? "start" : "middle"}>
                        {d == null ? "" : (d >= 0 ? "+" : "") + d.toFixed(3)}
                      </text>
                    );
                  })
                : key === "__conf"
                ? nodes.map((n, i) => {
                    const d = n.invert_rl != null ? n.invert_rl - compInvertAt(n.chainage) : null;
                    if (d == null) return null;
                    const over = Math.abs(d) > ilTol;
                    return (
                      <text key={i} x={i === 0 ? xOf(n.chainage) + 5 : xOf(n.chainage)} y={y + rowH - 7} fontSize="7.5" fontFamily="monospace" fontWeight="bold"
                        fill={over ? "#f87171" : "#34d399"} textAnchor={i === 0 ? "start" : "middle"}>{over ? "OVER" : "OK"}</text>
                    );
                  })
                : def.kind === "node"
                ? nodes.map((n, i) => (
                    <text key={i} x={i === 0 ? xOf(n.chainage) + 5 : xOf(n.chainage)} y={y + rowH - 7} fontSize="8" fontFamily="monospace" fill="#e4e4e7" textAnchor={i === 0 ? "start" : "middle"}>{def.val(n)}</text>
                  ))
                : segs.map((s, i) => (
                    <text key={i} x={xOf((s.start_chainage + s.end_chainage) / 2)} y={y + rowH - 7} fontSize="8" fontFamily="monospace" fill="#93c5fd" textAnchor="middle">{def.val(s)}</text>
                  ))}
            </g>
          );
        })}
        {/* opaque label column drawn on top so values never bleed under labels */}
        <rect x={0} y={0} width={mL} height={H} fill="#0b1f3a" />
        <rect x={0} y={headH} width={mL} height={H - headH} fill="#0d0d0d" />
        <line x1={mL} y1={0} x2={mL} y2={H} stroke="#334155" strokeWidth="1" />
        <text x={6} y={headH - 7} fontSize="9" fontFamily="monospace" fill="#cbd5e1">Pit / Node</text>
        {shown.map((key, r) => (
          <text key={key} x={6} y={headH + r * rowH + rowH - 7} fontSize="8.5" fontFamily="monospace" fill={key === "__delta" ? "#fbbf24" : key === "__conf" ? "#34d399" : "#a1a1aa"}>{labelOf(key)}</text>
        ))}
      </svg>
    </div>
  );
}

function CrossSectionPanel({ fileId, chain, nodes, existingSurf, designSurf }) {
  const [open, setOpen] = useState(false);
  const [nodeIdx, setNodeIdx] = useState("0");
  const [width, setWidth] = useState(10);
  const [interval, setInterval] = useState(1);
  const [xs, setXs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setXs(null); setNodeIdx("0"); }, [chain]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await computeCrossSection({
        file_id: fileId, asset_ids: chain, node_index: parseInt(nodeIdx),
        width: parseFloat(width) || 10, interval: parseFloat(interval) || 1,
        existing_surface_id: existingSurf !== "none" ? existingSurf : null,
        design_surface_id: designSurf !== "none" ? designSurf : null,
      });
      setXs(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cross-section failed");
      setXs(null);
    } finally { setBusy(false); }
  };

  const exportCSV = () => {
    if (!xs) return;
    const rows = [["offset_m", "easting", "northing", "existing_rl", "design_rl"]];
    xs.offsets.forEach((o, i) => rows.push([o, xs.points[i][0], xs.points[i][1], xs.existing_z?.[i] ?? "", xs.design_z?.[i] ?? ""]));
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `CrossSection_${xs.node.label.replace(/\s+/g, "_")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010]" data-testid="cross-section-panel">
      <button
        data-testid="cross-section-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400 hover:text-zinc-200"
      >
        <Scissors className="w-3.5 h-3.5" /> Cross-sections {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-mono text-[10px] text-zinc-500">Pit / node</label>
              <Select value={nodeIdx} onValueChange={setNodeIdx}>
                <SelectTrigger data-testid="xs-node-select" className="w-52 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
                  {nodes.map((n, i) => (
                    <SelectItem key={i} value={String(i)} className="font-mono text-[11px]">{n.label} · {n.chainage.toFixed(1)}m</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-mono text-[10px] text-zinc-500">Width (m)</label>
              <Input type="number" value={width} onChange={(e) => setWidth(e.target.value)} data-testid="xs-width" className="w-24 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-mono text-[10px] text-zinc-500">Interval (m)</label>
              <Input type="number" value={interval} onChange={(e) => setInterval(e.target.value)} data-testid="xs-interval" className="w-24 h-8 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]" />
            </div>
            <Button size="sm" onClick={generate} disabled={busy} data-testid="xs-generate-btn" className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5 mr-1.5" />} Generate
            </Button>
            {xs && (
              <Button size="sm" onClick={exportCSV} variant="outline" data-testid="xs-csv-btn" className="h-8 rounded-sm border-zinc-700 bg-transparent text-zinc-300">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            )}
          </div>
          {xs && <CrossSectionPlot xs={xs} />}
          {xs && (existingSurf === "none" && designSurf === "none") && (
            <p className="font-mono text-[10px] text-amber-500/80">No surface selected — plot shows pit geometry only. Pick an Existing/Design surface above to source ground levels across the section.</p>
          )}
        </div>
      )}
    </div>
  );
}

function CrossSectionPlot({ xs }) {
  const n = xs.node;
  const half = xs.width / 2;
  const exZ = xs.existing_z || [];
  const deZ = xs.design_z || [];
  const rls = [
    ...exZ.filter((v) => v != null),
    ...deZ.filter((v) => v != null),
    n.surface_rl, n.invert_rl,
  ].filter((v) => v != null);

  const mL = 52, mR = 16, mT = 18, mB = 36, innerW = 480, innerH = 200;
  const W = mL + innerW + mR, H = mT + innerH + mB;
  const hasRL = rls.length > 0;
  const minRL = hasRL ? Math.floor((Math.min(...rls) - 0.3) * 2) / 2 : 0;
  const maxRL = hasRL ? Math.ceil((Math.max(...rls) + 0.3) * 2) / 2 : 1;
  const xOf = (o) => mL + ((o + half) / (xs.width || 1)) * innerW;
  const yOf = (rl) => mT + (maxRL - rl) / ((maxRL - minRL) || 1) * innerH;

  const pathOf = (zs) => {
    let d = "", started = false;
    xs.offsets.forEach((o, i) => {
      const z = zs[i];
      if (z == null) { started = false; return; }
      d += `${started ? "L" : "M"}${xOf(o).toFixed(1)},${yOf(z).toFixed(1)} `;
      started = true;
    });
    return d.trim();
  };

  const ticks = [];
  for (let rl = minRL; rl <= maxRL + 1e-6; rl += 0.5) ticks.push(+rl.toFixed(2));
  const offTicks = xs.offsets.filter((_, i) => i % Math.max(1, Math.round(xs.offsets.length / 8)) === 0);

  return (
    <div className="space-y-2" data-testid="cross-section-result">
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-zinc-500">
        <span className="text-zinc-300">{n.label}</span>
        <span>Ch {n.chainage.toFixed(1)} m</span>
        {n.surface_rl != null && <span className="text-amber-500">SL {n.surface_rl.toFixed(3)}</span>}
        {n.invert_rl != null && <span className="text-cyan-400">IL {n.invert_rl.toFixed(3)}</span>}
        {xs.bearing != null && <span>section bearing {xs.bearing}°</span>}
        {xs.existing_name && <span className="text-emerald-400">existing: {xs.existing_name}</span>}
        {xs.design_name && <span className="text-purple-400">design: {xs.design_name}</span>}
      </div>
      <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] overflow-auto" data-testid="cross-section-svg">
        <svg width={W} height={H} style={{ display: "block" }}>
          {ticks.map((rl) => (
            <g key={rl}>
              <line x1={mL} y1={yOf(rl)} x2={mL + innerW} y2={yOf(rl)} stroke="#1f2937" strokeWidth="1" />
              <text x={mL - 6} y={yOf(rl) + 3} fontSize="8" fontFamily="monospace" fill="#71717a" textAnchor="end">{rl.toFixed(2)}</text>
            </g>
          ))}
          {offTicks.map((o) => (
            <g key={o}>
              <line x1={xOf(o)} y1={mT} x2={xOf(o)} y2={mT + innerH} stroke="#161b22" strokeWidth="1" />
              <text x={xOf(o)} y={mT + innerH + 14} fontSize="8" fontFamily="monospace" fill="#71717a" textAnchor="middle">{o > 0 ? `+${o}` : o}</text>
            </g>
          ))}
          <line x1={xOf(0)} y1={mT} x2={xOf(0)} y2={mT + innerH} stroke="#3f3f46" strokeWidth="1" strokeDasharray="3 3" />
          <text x={mL + innerW / 2} y={H - 6} fontSize="9" fontFamily="monospace" fill="#a1a1aa" textAnchor="middle">Offset from alignment (m)</text>
          <text x={12} y={mT + innerH / 2} fontSize="9" fontFamily="monospace" fill="#a1a1aa" textAnchor="middle" transform={`rotate(-90 12 ${mT + innerH / 2})`}>RL (m AHD)</text>

          {exZ.some((v) => v != null) && <path d={pathOf(exZ)} fill="none" stroke="#34d399" strokeWidth="1.8" />}
          {deZ.some((v) => v != null) && <path d={pathOf(deZ)} fill="none" stroke="#c084fc" strokeWidth="1.8" strokeDasharray="6 4" />}

          {/* pit at offset 0 */}
          {n.surface_rl != null && n.invert_rl != null && (
            <g>
              <rect x={xOf(0) - 5} y={yOf(n.surface_rl)} width={10} height={Math.max(2, yOf(n.invert_rl) - yOf(n.surface_rl))} fill="rgba(113,113,122,0.25)" stroke="#a1a1aa" strokeWidth="1.2" />
              <circle cx={xOf(0)} cy={yOf(n.surface_rl)} r="2.5" fill="#b45309" />
              <circle cx={xOf(0)} cy={yOf(n.invert_rl)} r="2.5" fill="#22d3ee" />
            </g>
          )}
        </svg>
      </div>
      <div className="border border-zinc-800 rounded-sm overflow-x-auto" data-testid="cross-section-table">
        <table className="w-full font-mono text-[11px]">
          <thead>
            <tr className="bg-[#101010] text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
              <th className="text-right font-medium px-3 py-1.5">Offset</th>
              <th className="text-right font-medium px-3 py-1.5">Easting</th>
              <th className="text-right font-medium px-3 py-1.5">Northing</th>
              <th className="text-right font-medium px-3 py-1.5">Existing RL</th>
              <th className="text-right font-medium px-3 py-1.5">Design RL</th>
            </tr>
          </thead>
          <tbody>
            {xs.offsets.map((o, i) => (
              <tr key={i} className="border-b border-zinc-900 last:border-0">
                <td className="px-3 py-1 text-right text-zinc-300">{o > 0 ? `+${o}` : o}</td>
                <td className="px-3 py-1 text-right text-zinc-400">{xs.points[i][0].toFixed(3)}</td>
                <td className="px-3 py-1 text-right text-zinc-400">{xs.points[i][1].toFixed(3)}</td>
                <td className="px-3 py-1 text-right text-emerald-400">{exZ[i] != null ? exZ[i].toFixed(3) : "—"}</td>
                <td className="px-3 py-1 text-right text-purple-400">{deZ[i] != null ? deZ[i].toFixed(3) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
